// PLOW — the weekly client report.
//
// Some managers send a client a "plan of works" sheet each week: one row per
// job, with the site, the project, Yes/No against each day of the week, the AV
// reference, planned dates and a line of description. This module turns one
// week of a board into those rows. It knows nothing about the screen or the
// spreadsheet library, so the grouping and the rows are tested by running it.
//
// A "task" here is one assigned cell: a person, a date and a section of the
// day, carrying a work job. Tasks are grouped by the job's AV reference, and
// the manager ticks or unticks them in the dialog before the sheet is built.

// The client's own column order. Kept verbatim (including "Thur") because the
// sheet is pasted into their template.
export const PLOW_EXPORT_HEADERS = [
  'Building', 'Area', 'Zone', 'Project Name', 'Contractor', 'Project Contact', 'CSO', 'Handover Doc',
  'Mon', 'Tue', 'Wed', 'Thur', 'Fri', 'Sat', 'Sun',
  'Job', 'Pack Y/N', 'Permit Y/N', 'Planned start', 'Planned Finish', 'Status', 'White Badge',
  'Brief Description of works', 'Escort', 'Category', 'Works Manager',
];

// Columns the board holds nothing for. They stay in the sheet, blank, so the
// manager fills them in rather than re-adding them.
const BLANK = '';

// A board row is a person or a label ("SERVICING"), and a cell belongs to
// whichever its row is, so the key is the row rather than the person. For a
// person's cell this is the same string it always was.
export const rowKeyOf = (x) => x.person_id || x.member_id || x.id;
export const taskKey = (s) => `${rowKeyOf(s)}|${s.slot_date}|${s.section_id}`;

// Monday = 0 … Sunday = 6, from an ISO date, in UTC like the rest of the app.
const weekdayIndex = (iso) => (new Date(`${iso}T00:00:00Z`).getUTCDay() + 6) % 7;

// A real Date for Excel, at UTC noon so no timezone shifts the day.
const xlDate = (iso) => {
  const m = String(iso).match(/^(\d{4})-(\d{2})-(\d{2})$/);
  return m ? new Date(Date.UTC(+m[1], +m[2] - 1, +m[3], 12)) : null;
};

const personLabel = (m) => (m && (m.display_label || m.person_name || m.label)) || '?';

/**
 * Group a week's assigned cells by the AV reference of their job.
 *
 * @param {object} week  The week response (dates, sections, members, jobs,
 *   slots) plus `sowDetails`: the linked SOW rows from GET /plows/:id/linked-sows.
 * @returns {{ groups: Array, unlinked: Array }}
 *   groups: one per linked work job with at least one assignment this week,
 *   ordered by reference, each `{ job, reference, sow, tasks }`.
 *   unlinked: work jobs assigned this week that carry no AV reference, so the
 *   dialog can say what the sheet leaves out.
 */
export function groupExportTasks({ dates = [], sections = [], members = [], jobs = [], slots = [], label_tasks = [], sowDetails = [] }) {
  const jobById = Object.fromEntries(jobs.map(j => [j.id, j]));
  const sowById = Object.fromEntries(sowDetails.map(s => [s.sow_id, s]));
  const sectionById = Object.fromEntries(sections.map(s => [s.id, s]));
  const sectionOrder = Object.fromEntries(sections.map((s, i) => [s.id, i]));
  const memberById = Object.fromEntries(members.map(m => [rowKeyOf(m), m]));
  const memberOrder = Object.fromEntries(members.map((m, i) => [rowKeyOf(m), i]));
  const dateSet = new Set(dates);

  const byJob = new Map();
  const unlinkedById = new Map();
  const add = (job, task) => {
    if (!job || job.kind !== 'work') return;
    if (!job.sow_id || !job.sow_reference) {
      unlinkedById.set(job.id, job);
      return;
    }
    if (!byJob.has(job.id)) byJob.set(job.id, []);
    byJob.get(job.id).push(task);
  };
  for (const s of slots) {
    if (!s.job_id) continue;
    if (dateSet.size && !dateSet.has(s.slot_date)) continue;
    const section = sectionById[s.section_id];
    add(jobById[s.job_id], {
      key: taskKey(s),
      person_id: s.person_id,
      row_id: rowKeyOf(s),
      person_name: personLabel(memberById[rowKeyOf(s)]),
      slot_date: s.slot_date,
      section_id: s.section_id,
      section_label: section?.label || '',
      note: s.note || '',
      rag: s.rag || null,
    });
  }
  // A label row's day is a list of tasks with no section. Each is its own row
  // in the sheet, keyed by its own id, because two service calls on the same
  // job the same day are two visits.
  for (const t of label_tasks) {
    if (!t.job_id) continue;
    if (dateSet.size && !dateSet.has(t.task_date)) continue;
    add(jobById[t.job_id], {
      key: `task|${t.id}`,
      person_id: null,
      row_id: t.member_id,
      person_name: personLabel(memberById[t.member_id]),
      slot_date: t.task_date,
      section_id: null,
      section_label: '',
      sort_order: t.sort_order ?? 0,
      note: t.note || '',
      rag: t.rag || null,
    });
  }

  const order = (id, map) => (map[id] === undefined ? Number.MAX_SAFE_INTEGER : map[id]);
  const groups = [...byJob.entries()].map(([jobId, tasks]) => {
    const job = jobById[jobId];
    tasks.sort((a, b) =>
      a.slot_date.localeCompare(b.slot_date)
      || order(a.row_id, memberOrder) - order(b.row_id, memberOrder)
      || order(a.section_id, sectionOrder) - order(b.section_id, sectionOrder)
      || (a.sort_order ?? 0) - (b.sort_order ?? 0));
    return {
      job,
      reference: job.sow_reference,
      sow: sowById[job.sow_id] || { sow_id: job.sow_id, reference: job.sow_reference },
      tasks,
    };
  });
  groups.sort((a, b) => a.reference.localeCompare(b.reference) || a.job.name.localeCompare(b.job.name));

  const unlinked = [...unlinkedById.values()].sort((a, b) => a.name.localeCompare(b.name));
  return { groups, unlinked };
}

/**
 * Build the sheet's data rows (no header) from the groups and the ticked task
 * keys. A job with nothing ticked is left out; a day is "Yes" when a ticked
 * task falls on it; planned start and finish are the first and last ticked
 * days. Dates are Date objects so Excel treats them as dates.
 */
export function buildExportRows({ groups = [], selected = new Set() }) {
  const rows = [];
  for (const g of groups) {
    const tasks = g.tasks.filter(t => selected.has(t.key));
    if (!tasks.length) continue;
    const days = Array(7).fill('No');
    for (const t of tasks) days[weekdayIndex(t.slot_date)] = 'Yes';
    const sorted = tasks.map(t => t.slot_date).sort();
    const sow = g.sow || {};
    rows.push([
      sow.location || BLANK,                              // Building
      BLANK,                                              // Area
      BLANK,                                              // Zone
      sow.detail || g.job.name || BLANK,                  // Project Name
      'Alton Valley',                                     // Contractor
      sow.client_contact_name || sow.contact || BLANK,    // Project Contact
      BLANK,                                              // CSO
      BLANK,                                              // Handover Doc
      ...days,                                            // Mon … Sun
      g.reference,                                        // Job
      BLANK,                                              // Pack Y/N
      BLANK,                                              // Permit Y/N
      xlDate(sorted[0]),                                  // Planned start
      xlDate(sorted[sorted.length - 1]),                  // Planned Finish
      sow.status || BLANK,                                // Status
      BLANK,                                              // White Badge
      g.job.detail || g.job.name || BLANK,                // Brief Description of works
      BLANK,                                              // Escort
      BLANK,                                              // Category
      sow.delivery_owner_name || sow.owner_name || BLANK, // Works Manager
    ]);
  }
  return rows;
}
